<template>
  <div
    class="payment-card__container"
    id="payment-card"
    tabindex="0"
    @click="$emit('selectPayment', payment)"
    @keydown.enter="$emit('selectPayment', payment)"
  >
    <div class="payment-card__content">
      <div class="payment-card__logo-container">
        <img class="payment-card__logo" :src="imageSrc" />
      </div>

      <div
        :id="payment.id"
        :data-payment-name="payment.code"
        :data-payment-provider="payment.paymentType"
        class="payment-card__description"
      >
        <p class="payment-card__name">{{ payment.name }}</p>

        <span class="payment-card__fee"
        >Gateway&nbsp;Fee&nbsp;{{ gatewayFee }}

          <Tooltip v-if="shouldShowBenefits && !hideDescription">
            <template #tooltip-elem>
              <Info
                class="payment-card__fee--icon"
                :iconColor="computedIconColor"
              />
            </template>
            <template #tooltip-text>
              <ul class="payment-card__benefits">
                <p>Benefits</p>
                <li v-for="benefit in benefits" :key="benefit">
                  {{ benefit }}
                </li>
              </ul>
            </template>
          </Tooltip>
        </span>
      </div>
      <CheckCircle />
    </div>
  </div>
</template>

<script setup>
// ====================================
// Storage
// ====================================
import { computed, toRef } from 'vue';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { useGlobalStore } from '@/stores/globalStore';
// ====================================
// Composables
// ====================================
import usePaymentFilters from '@/composables/filters/usePaymentFilters';
import {
  toKebabCase,
  toCurrencyFormat,
} from '@/composables/helpers/useStringHelpers';
import { checkGeo } from '@/composables/helpers/useDetectedGeo';
// ====================================
// Components
// ====================================
import CheckCircle from '@/components/icons/CheckCircle.vue';
import Info from '@/components/icons/Info.vue';
import Tooltip from '@/components/ui/tooltips/Tooltip.vue';

const orderStore = useOrderFormStore();
const globalStore = useGlobalStore();
const { filterPaymentDataByPaymentName, filterPaymentLimit } = usePaymentFilters();

const paymentsWithGenericCCImg = ['worldpaycredit', 'checkoutcredit', 'primercc'];
const paymentsWithBankTransferImg = ['primetrust'];
const paymentsWithSvgPreference = ['worldpaycredit', 'dlocalspei'];
const geoIsUK = computed(() => checkGeo('GB'));
const props = defineProps({
  payment: {
    type: Object,
    required: true,
  },
  defaultPayment: {
    type: Object,
    required: true,
  },
  hideDescription: {
    // used for NFT
    type: Boolean,
    default: false,
  },
});
defineEmits(['selectPayment']);

const mapCodeToIconPath = (code) => {
  if (paymentsWithGenericCCImg.includes(code)) {
    return 'creditcard';
  }

  if (paymentsWithBankTransferImg.includes(code)) {
    return 'banktransfer';
  }

  return code;
};

const getPaymentDetails = () => {
  const { code } = props.payment;
  const paymentMethod = filterPaymentDataByPaymentName(
    orderStore.getActiveStorePaymentData,
    code,
  );
  const transactionLimits = filterPaymentLimit(
    orderStore.getActiveStore.getFiatType,
    code,
    orderStore.getActiveStorePaymentData,
  );
  // when transaction limit is not defined, the payment method is not selected
  if (paymentMethod.length == 0 || !transactionLimits) {
    return {
      requirements: [],
      benefits: [],
    };
  }

  return {
    requirements: paymentMethod[0].requirements,
    benefits: [
      `Up to ${toCurrencyFormat(
        transactionLimits.weekly,
        transactionLimits.fiat_code,
      ).slice(0, -3)} ${transactionLimits.fiat_code} a week`,
      ...paymentMethod[0].benefits,
    ],
  };
};

const gatewayFee = computed(() => {
  const showZeroFeeData = '0%';
  const activeFiat = orderStore.getFiatType;
  const activeCoin = orderStore.getCoinType;
  const correspondingFees = props.payment.transaction_fees.find(
    (fee) => fee.fiat_code === activeFiat && fee.coin_code === activeCoin,
  );

  if (!correspondingFees || correspondingFees.fees.length === 0) {
    return showZeroFeeData;
  }

  const computedIconColor = computed(() => (globalStore.theme === 'DARK' ? '#fff' : 'rgb(17, 22, 48)'));

  // fee property is an array
  const fee = correspondingFees.fees[0]?.amount;
  const feeIsPercentage = correspondingFees.fees[0]?.type === 'percentage';
  const transactionLimits = filterPaymentLimit(
    orderStore.getActiveStore.getFiatType,
    orderStore.getActiveStore.getFormPayload.payment,
    orderStore.getActiveStorePaymentData,
  );

  if (feeIsPercentage) {
    return fee ? `${(fee * 100).toFixed(2)}%` : showZeroFeeData;
  }
  // flat fee
  return fee && transactionLimits
    ? `${toCurrencyFormat(fee, transactionLimits.fiat_code)}`
    : '0';
});

const imageSrc = computed(() => {
  const extension = paymentsWithSvgPreference.includes(
    props.payment.code.toLowerCase(),
  )
    ? 'svg'
    : 'png';

  const path = toKebabCase(
    `/images/logos/payments/${mapCodeToIconPath(
      props.payment.paymentType.toLowerCase(),
    )}.${extension}`,
  );
  return path;
});

const benefits = computed(() => getPaymentDetails().benefits);

const shouldShowBenefits = computed(
  () => getPaymentDetails().benefits.length > 0 && !geoIsUK.value,
);

</script>

<style lang="scss">
.payment-card {
  &__container {
    position: relative;
    max-height: 125px;
    width: 100%;
    padding: 10px 10px 2px 10px;
    display: flex;
    justify-content: center;
    transition: transform 250ms;
    border-radius: $border-radius;
    background: var(--input-background);
    transition: background 250ms ease;
    cursor: pointer;

    &.--is-selected,
    &:hover {
      background-color: var(--payment-card-hover);
    }

    &.--is-selected {
      #check {
        opacity: 1;
        transition: opacity 100ms ease;
      }
    }
  }

  &__content {
    padding-top: 0.5rem;
    width: 100%;
  }

  #check {
    position: absolute;
    right: 15px;
    top: 10px;
    opacity: 0;
    width: 1.5rem;
    path {
      fill: var(--text-color);
    }
  }

  &__logo-container {
    position: relative;
    display: flex;
    align-content: center;
    justify-content: center;
    width: 6rem;
    height: 3.4rem;
    background: var(--payment-logo-background);
    border-radius: calc($border-radius / 2);
  }

  &__logo {
    height: calc(100% - 1rem);
    width: calc(100% - 2rem);
    object-fit: contain;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__description {
    display: flex;
    flex-direction: column;
    padding-block: 0.5rem;
  }

  &__name {
    line-height: 1.1;
    @include breakpoint($min-m) {
      margin-bottom: -5px; // to account for the icon showing
    }
  }

  &__fee {
    display: flex;
    align-items: center;
    padding: 0.6rem 0 0;

    &--icon {
      display: none;
      cursor: pointer;
      margin-left: 5px;
      width: 10px;
      height: 10px;
      stroke: var(--text-color);
      background: transparent;

      @media screen and (min-width: 769px) {
        display: block;
      }
    }
  }

  &__benefits {
    list-style: disc;
    text-align: left;
    padding: 0.5rem 1rem;
    list-style-position: inside;
  }
}

.nft-layout {
  .payment-card__fee {
    margin-top: 0.5rem;
  }
}
</style>
